import * as React from 'react';
import { graphql } from 'react-apollo';

import { compose, withProps } from 'recompose';

import DiscoveryCollectionsQuery from 'bundles/browse/components/DiscoveryCollectionsQuery.graphql';
import type {
  DiscoveryCollectionsQuery as DiscoveryCollectionsQueryData,
  DiscoveryCollectionsQueryVariables,
} from 'bundles/browse/components/__generated__/DiscoveryCollectionsQuery';
import type { DiscoveryCollection } from 'bundles/browse/components/types/DiscoveryCollections';
import { CONSUMER_PAGE_CONTENT_CONTENTFUL_SLUG } from 'bundles/unified-career-academy/constants';
import PageContentContext from 'bundles/unified-career-academy/contexts/PageContentContext';
import RecommendationsContext from 'bundles/unified-career-academy/contexts/RecommendationsContext';
import withUnifiedCareerAcademyContentfulData from 'bundles/unified-career-academy/hocs/withUnifiedCareerAcademyContentfulData';
import type { PropsFromContentful } from 'bundles/unified-career-academy/hocs/withUnifiedCareerAcademyContentfulData';

type PropsFromCaller = { children: React.ReactNode };

type PropsFromDiscoveryCollections = {
  discoveryCollections?: Array<DiscoveryCollection | null>;
};

type PropsToComponent = PropsFromCaller & PropsFromContentful & PropsFromDiscoveryCollections;

const ConsumerDataProvider = ({ children, pageContent, discoveryCollections }: PropsToComponent) => {
  return (
    <PageContentContext.Provider value={pageContent}>
      <RecommendationsContext.Provider value={discoveryCollections}>{children}</RecommendationsContext.Provider>
    </PageContentContext.Provider>
  );
};

export default compose<PropsToComponent, PropsFromCaller>(
  withProps(() => ({
    contentfulSlug: CONSUMER_PAGE_CONTENT_CONTENTFUL_SLUG,
  })),
  withUnifiedCareerAcademyContentfulData(),
  graphql<
    PropsFromContentful,
    DiscoveryCollectionsQueryData,
    DiscoveryCollectionsQueryVariables,
    PropsFromDiscoveryCollections
  >(DiscoveryCollectionsQuery, {
    skip: ({ pageContent }) =>
      !pageContent?.recommendationsCollectionSetId || !pageContent?.recommendationsCollectionSetType,
    options: ({ pageContent }) => {
      // The fallback here will never be used due to the skip condition above. This is a bug with react-apollo and ts.
      return {
        variables: {
          contextId: pageContent?.recommendationsCollectionSetId || 'career-academy',
          contextType: pageContent?.recommendationsCollectionSetType || 'PAGE',
        },
        context: { clientName: 'gatewayGql' },
      };
    },
    props: ({ data }) => {
      return {
        discoveryCollections: data?.DiscoveryCollections?.queryCollections
          ? data.DiscoveryCollections.queryCollections
          : undefined,
      };
    },
  })
)(ConsumerDataProvider);
